import { Children, cloneElement, FC, isValidElement, ReactElement } from 'react';

import {
  TabsProps as MantineTabsProps,
  Tabs as MantineTabs,
  MantineColor
} from '@mantine/core';

import { useMergeClassNames } from '../../utils';

import { Tab, TabProps } from './Tab';
import classes from './Tabs.module.css';
import { TabsPanel, TabsPanelProps } from './TabsPanel';

interface TabsProps extends MantineTabsProps {
  fullHeight?: boolean;
  bg?: MantineColor;
}

type TabsComponent<P> = FC<P> & {
  List: typeof MantineTabs.List;
  Tab: typeof Tab;
  Panel: typeof TabsPanel;
};

const Tabs: TabsComponent<TabsProps> = ({
  children,
  color = 'primary',
  variant = 'default',
  bg,
  classNames,
  fullHeight,
  ...rest
}) => {
  const childrenWithProps = Children.map(
    children as ReactElement<TabProps | TabsPanelProps>[],
    (child: ReactElement<TabProps | TabsPanelProps>) => {
      if (isValidElement(child) && typeof child.type === typeof TabsPanel) {
        return cloneElement(child, { fullHeight, bg, ...child.props });
      }
      return child;
    }
  );

  const mergedClassNames = useMergeClassNames({
    classNames: [
      {
        tab: classes.tab,
        tabLabel: classes.tabLabel,
        tabsList: classes.tabsList,
        tabSection: classes.tabIcon
      },
      classNames
    ]
  });

  return (
    <MantineTabs
      {...rest}
      color={color}
      classNames={mergedClassNames}
      variant={variant}
      h={fullHeight ? '100%' : 'auto'}
    >
      {childrenWithProps}
    </MantineTabs>
  );
};

Tabs.List = MantineTabs.List;
Tabs.Panel = TabsPanel;
Tabs.Tab = Tab;

export { Tabs };
export type { TabsProps, TabProps };
